import React, { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { CloseOutSetOffAndDefault, getOpenFieldsAndSections, toggleOpenFieldSection, UpdatedOpinionField } from '../store';
import { SectionWrapper } from './SectionWrapper';
import { SingleField } from '../fields/SingleField';
import styles from './OpinionSection.module.scss';

interface ClientRelianceCloseOutSetOffAndDefaultProps {
    closeOutSetOffAndDefault: CloseOutSetOffAndDefault;
    isEditing: boolean;
    isUpdating: boolean;
    fieldsUpdated: UpdatedOpinionField[];
}

export const ClientRelianceCloseOutSetOffAndDefault: React.FC<ClientRelianceCloseOutSetOffAndDefaultProps> = ({ closeOutSetOffAndDefault, isEditing, isUpdating, fieldsUpdated }) => {
    const dispatch = useAppDispatch();
    const openFieldsAndSections = useAppSelector(getOpenFieldsAndSections);
    const getFieldOpen = useCallback((id: string) => openFieldsAndSections.includes(id), [openFieldsAndSections]);
    const toggleFieldOpen = useCallback((id: string) => dispatch(toggleOpenFieldSection(id)), [dispatch]);
    const sectionId = 'closeOutSetOffAndDefault';
    const { cmTriggerEventConsequenceProvisionsEnforceable, conclusionsApplyEqually, provisionsOfCCPDefaultEnforceable, hierarchyOfApplicableEventsEnforceable, setOffProvisionsEnforceable, limitedRecourseProvisionsEnforceable, addendumAdverselyImpactRuleSet, addendumAdverselyImpactISDANettingConclusions, isdaCollateralProviderConclusionsApplyEqually, paragraphsAdverselyImpactConclusions, amendedAddendumEnforceable } = closeOutSetOffAndDefault;

    return (
        <SectionWrapper id={sectionId} label='Close-out, Set-off, and Default' isEditing={isEditing} isUpdating={isUpdating}>
            <>
                <div className={styles.sectionHeaderLabel}>Under the laws of the jurisdiction under consideration:</div>
                <SingleField
                    field={cmTriggerEventConsequenceProvisionsEnforceable}
                    fieldId='cmTriggerEventConsequenceProvisionsEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={conclusionsApplyEqually}
                    fieldId='conclusionsApplyEqually'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={provisionsOfCCPDefaultEnforceable}
                    fieldId='provisionsOfCCPDefaultEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={hierarchyOfApplicableEventsEnforceable}
                    fieldId='hierarchyOfApplicableEventsEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={setOffProvisionsEnforceable}
                    fieldId='setOffProvisionsEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={limitedRecourseProvisionsEnforceable}
                    fieldId='limitedRecourseProvisionsEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={addendumAdverselyImpactRuleSet}
                    fieldId='addendumAdverselyImpactRuleSet'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={addendumAdverselyImpactISDANettingConclusions}
                    fieldId='addendumAdverselyImpactISDANettingConclusions'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={isdaCollateralProviderConclusionsApplyEqually}
                    fieldId='isdaCollateralProviderConclusionsApplyEqually'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={paragraphsAdverselyImpactConclusions}
                    fieldId='paragraphsAdverselyImpactConclusions'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={amendedAddendumEnforceable}
                    fieldId='amendedAddendumEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
            </>
        </SectionWrapper>
    );
};
